import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import apiClient from '@app/config/axios.config'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from './useFeatureFlags'

export type DamageCostReason = 'no_damage_ratio_data' | 'missing_replacement_cost'

export interface DamageCostResponse {
  asset_id?: number
  hazard_id: string
  damage_cost_min: number | null
  damage_cost_max: number | null
  scenario_count?: number
  asset_count?: number
  missing_assets?: number
  reason?: DamageCostReason
}

/** Imperative fetch — for callers that render to a one-shot HTML string (e.g. Mapbox popup). */
export const fetchDamageCostForSelectedElement = async (
  element_layer_id: string,
  asset_id: number,
  hazard_scenario: string,
): Promise<DamageCostResponse> => {
  const res = await apiClient.get<DamageCostResponse>(
    `/asset/damage_cost/selected_element/${element_layer_id}/${asset_id}/${hazard_scenario}`,
  )
  return res.data
}

/** Damage cost for a single (asset, hazard) — used by the selected-element card. */
export const useDamageCostForSelectedElement = (params: {
  element_layer_id: string | undefined
  asset_id: number | undefined
  hazard_scenario: string | undefined
}): UseQueryResult<DamageCostResponse> => {
  const { useFeatureFlag } = useFeatureFlags()
  const enabled =
    useFeatureFlag(FEATURE_FLAGS.DAMAGE_COST_REPORTING) &&
    !!params.element_layer_id &&
    params.asset_id !== undefined &&
    !!params.hazard_scenario

  return useQuery({
    queryKey: [
      'damage_cost',
      'selectedElement',
      params.element_layer_id,
      params.asset_id,
      params.hazard_scenario,
    ],
    queryFn: () =>
      fetchDamageCostForSelectedElement(
        params.element_layer_id as string,
        params.asset_id as number,
        params.hazard_scenario as string,
      ),
    enabled,
  })
}

/**
 * Hazard shape expected by the map hover tooltip — just enough to identify the
 * hazard and pass its scenario context to the damage cost endpoint. Callers also use
 * other fields (vulnerability label, exposure, etc.) but those are unrelated
 * to the damage cost enrichment performed here.
 */
type HoverHazard = {
  hazard_scenario?: string
}

/**
 * Fetches damage cost for every hazard the asset is exposed to (in parallel) and
 * returns each hazard enriched with `damage_cost_min`/`damage_cost_max`/`damage_cost_reason`. Failures
 * fall through to a `no_damage_ratio_data` N/A row so the tooltip still
 * surfaces the feature line rather than silently dropping it.
 *
 * Intended for the map hover popup, which is rendered to a static HTML string
 * via `ReactDOMServer.renderToString` and so can't use the React-Query hook.
 */
export const enrichHazardsWithDamageCost = async <T extends HoverHazard>(
  element_layer_id: string,
  asset_id: number,
  hazards: T[],
): Promise<
  (T & {
    damage_cost_min: number | null
    damage_cost_max: number | null
    damage_cost_reason?: DamageCostReason
  })[]
> => {
  const fallback = {
    damage_cost_min: null,
    damage_cost_max: null,
    reason: 'no_damage_ratio_data' as DamageCostReason,
  }

  const results = await Promise.all(
    hazards.map((hazard) =>
      hazard.hazard_scenario
        ? fetchDamageCostForSelectedElement(
            element_layer_id,
            asset_id,
            hazard.hazard_scenario,
          ).catch(() => fallback)
        : Promise.resolve(fallback),
    ),
  )

  return hazards.map((hazard, index) => ({
    ...hazard,
    damage_cost_min: results[index].damage_cost_min ?? null,
    damage_cost_max: results[index].damage_cost_max ?? null,
    damage_cost_reason: results[index].reason,
  }))
}
